import type { IndexDateResolution } from '@verantos-dev/types-cohorts';

export type IndexDateSelectionOccurrence = 'first' | 'last' | 'nth';

export interface IndexDateSelection {
  /** `absolute`: the occurrence on its own. `rolling`: the occurrence at which every other criterion is also met. */
  mode: 'absolute' | 'rolling';
  occurrence: IndexDateSelectionOccurrence;
  /** The nth value, used when `occurrence` is `nth`. */
  nthValue: number;
}

const getOccurrence = (resolution: IndexDateResolution): IndexDateSelectionOccurrence => {
  if (resolution.occurrence === 'last') return 'last';
  if (resolution.occurrence === 'nth' && resolution.nthValue) return 'nth';
  return 'first';
};

/**
 * Describes how a boolean index date is picked, for the card's selection sentence.
 *
 * - Occurrence: first, last, or the nth.
 * - Mode: `dependentCriteriaMode` 'none' is the absolute occurrence; 'all' is the
 *   occurrence at which every other criterion is also met.
 *
 * A null/absent `dependentCriteriaMode` means no rule was recorded, which is how a
 * patient-level date index is stored (no occurrence to pick), so null is returned.
 */
export const getIndexDateSelection = (
  resolution: IndexDateResolution | null | undefined
): IndexDateSelection | null => {
  if (!resolution?.dependentCriteriaMode) return null;

  return {
    mode: resolution.dependentCriteriaMode === 'all' ? 'rolling' : 'absolute',
    occurrence: getOccurrence(resolution),
    nthValue: resolution.nthValue ?? 0,
  };
};
